import React, { useEffect, useMemo, useState } from 'react';
import { Card, Box, Typography, Skeleton } from '@mui/material';
import { Icon } from '@iconify/react';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getBestTeamperformance } from '../../../../../redux/features/poinTrackerSlice';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { Stack } from '@mui/material';

export default function BestTeamListCard({ data, loading, onRefresh, refreshing, lastUpdated }) {
  const bestTeam = data?.data ?? {};

  const teams =
    bestTeam?.leaderboard?.map((item) => ({
      rank: item.wing_id,
      name: item.name,
      score: item.trends?.revenue,
      growth: item.trends?.admissions,
      revenue: `₹${Number(item.revenue || 0).toLocaleString('en-IN')}`,
      users: item.admissions,
    })) || [];

  const noData = !loading && (!teams || teams.length === 0);

  return (
    <Card
      sx={{
        width: '100%',
        p: 3,
        borderRadius: 4,
        backgroundColor: '#f5f0f0',
        border: '2px solid #f28b82',
      }}
    >
      {/* Title */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" fontWeight="bold">
          Best Team <span style={{ color: 'red' }}>(Live)</span>
        </Typography>
        <Typography fontSize={12} color="gray">
          Updated {lastUpdated}
        </Typography>
        <Icon
          icon="mdi:refresh"
          width="20"
          height="20"
          style={{
            cursor: 'pointer',
            opacity: refreshing ? 0.5 : 1,
            transform: refreshing ? 'rotate(180deg)' : 'none',
            transition: 'all 0.3s ease',
          }}
          onClick={onRefresh}
        />
      </Box>

      {loading && !refreshing ? (
        <>
          {/* 🔥 Skeleton List */}
          {[...Array(4)].map((_, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 2,
                mb: 2,
                borderRadius: 3,
                backgroundColor: '#ffffff',
              }}
            >
              {/* Left */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={35} height={35} animation="wave" />

                <Box>
                  <Skeleton width={120} height={20} animation="wave" />
                  <Skeleton width={100} height={15} animation="wave" />
                </Box>
              </Box>

              {/* Right */}
              <Box textAlign="right">
                <Skeleton width={100} height={20} animation="wave" />
                <Skeleton width={60} height={15} animation="wave" />
              </Box>
            </Box>
          ))}
        </>
      ) : noData ? (
        <Box
          sx={{
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography color="text.secondary">No Data Found</Typography>
        </Box>
      ) : (
        <>
          {teams.map((team) => (
            <Box
              key={team.rank}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#ffffff',
                p: 2,
                mb: 2,
                borderRadius: 3,
              }}
            >
              {/* Left Side */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Rank Circle */}
                <Box
                  sx={{
                    width: 35,
                    height: 35,
                    borderRadius: '50%',
                    backgroundColor: '#4f6df5',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {team.rank}
                </Box>

                {/* Team Info */}
                <Box>
                  <Typography fontWeight="bold">{team.name}</Typography>

                  {/* Score + Growth */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <Icon icon="lucide:trophy" width="14" height="14" style={{ color: '#c88700' }} />

                    {team.score}

                    {team.growth !== undefined && team.growth !== null && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {team.growth > 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 14, color: 'green' }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 14, color: 'red' }} />
                        )}

                        <Typography variant="caption" sx={{ color: team.growth > 0 ? 'green' : 'red' }}>
                          {team.growth}%
                        </Typography>
                      </Stack>
                    )}
                  </Typography>
                </Box>
              </Box>

              {/* Right Side */}
              <Box textAlign="right">
                <Typography fontWeight="bold" sx={{ color: 'green' }}>
                  {team.revenue}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}
                >
                  <Icon icon="f7:person-2" width="14" height="14" />

                  {team.users !== undefined && team.users !== null && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="caption" sx={{ color: team.users > 0 ? 'green' : 'red' }}>
                        {team.users}%
                      </Typography>
                      {team.users > 0 ? (
                        <TrendingUpIcon sx={{ fontSize: 14, color: 'green' }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 14, color: 'red' }} />
                      )}
                    </Stack>
                  )}
                </Typography>
              </Box>
            </Box>
          ))}
        </>
      )}
    </Card>
  );
}
